import type { Doc } from "@repo/convex/dataModel";
import { X } from "lucide-react";
import type { KeyboardEvent } from "react";
import { Button } from "@/components/ui/button";
import { UnreadDot } from "@/components/ui/unread-dot";
import { cn } from "@/lib/utils";
import {
  formatTimeAgo,
  getNotificationIcon,
  getNotificationIconColor,
} from "../lib/notification-helpers";

// ============================================================================
// NotificationItem
// ============================================================================
// Renders a single notification row in the popover list.

interface NotificationItemProps {
  notification: Doc<"notifications">;
  projectName: string | undefined;
  onNavigate: (notification: Doc<"notifications">) => void;
  onRemove: (notificationId: Doc<"notifications">["_id"]) => void;
}

export function NotificationItem({
  notification,
  projectName,
  onNavigate,
  onRemove,
}: NotificationItemProps) {
  const Icon = getNotificationIcon(notification.type);
  const iconColor = getNotificationIconColor(notification.type);
  const isUnread = !notification.readAt;
  const handleRowKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    onNavigate(notification);
  };

  return (
    /* biome-ignore lint/a11y/useSemanticElements: The row is a composite target with its own nested remove button. */
    <div
      role="button"
      tabIndex={0}
      className={cn(
        "group/item flex w-full items-start gap-3 rounded-md px-2 py-2 text-left transition-colors",
        "no-touch:hover:bg-muted/50 touch:active:bg-muted/50",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        isUnread && "bg-muted/30"
      )}
      onClick={() => onNavigate(notification)}
      onKeyDown={handleRowKeyDown}
    >
      {/* Icon */}
      <span className={cn("mt-0.5 shrink-0", iconColor)}>
        <Icon className="size-4" />
      </span>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p
          className={cn("text-xs leading-snug", isUnread ? "font-medium" : "text-muted-foreground")}
        >
          {notification.title}
        </p>
        <p className="mt-0.5 text-xs leading-snug text-muted-foreground line-clamp-2">
          {notification.message}
        </p>
        <p className="mt-1 text-[0.625rem] text-muted-foreground/70">
          <span>{formatTimeAgo(notification._creationTime)}</span>
          {projectName && (
            <>
              <span className="mx-1.5">·</span>
              <span>{projectName}</span>
            </>
          )}
        </p>
      </div>

      {/* Unread dot + remove button */}
      <div className="flex shrink-0 items-center gap-1">
        {isUnread && <UnreadDot />}
        <Button
          variant="ghost"
          size="icon-xs"
          className="opacity-0 no-touch:group-hover/item:opacity-100 focus-visible:opacity-100"
          aria-label="Remove notification"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(notification._id);
          }}
        >
          <X className="size-3" />
        </Button>
      </div>
    </div>
  );
}
